import React from "react";
import DetailsCard from "../../DetailsCard";
import {AudienceModel} from "../../../redux/inAppMessageTypes";
import {createUseStyles} from "react-jss";
import {Colors} from "../../../constants/colors";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faBuilding, faGlobe} from "@fortawesome/free-solid-svg-icons";

type Props = {
  audience?: AudienceModel;
  canEdit: boolean;
  onEdit?: () => void;
};

const AudienceCard = ({audience, canEdit, onEdit}: Props) => {
  const styles = useStyles();
  const onClick = canEdit && onEdit ? onEdit : undefined;

  const renderAudienceInfo = () => {
    if (!audience) {
      return (
        <p>
          <span>No audience defined.</span> This message will not be displayed to anyone until an audience is configured.
        </p>
      );
    }

    switch (audience.type) {
      case 'employer':
        return (
          <div className={styles.audienceSection}>
            <div className={styles.audienceType}>
              <FontAwesomeIcon icon={faBuilding} className={styles.audienceIcon}/>
              <span className={styles.audienceLabel}>
              {audience.employers.length} Employer{audience.employers.length > 1 ? 's' : ''}
            </span>
            </div>
            <p className={styles.audienceDescription}>
              This message will be displayed to users associated with the selected employers.
            </p>
            <div className={styles.employerList}>
              {audience.employers.map((emp, index) => (
                <div key={emp.id} className={styles.employerChip}>
                  {emp.name}
                </div>
              ))}
            </div>
          </div>
        );
      case 'allUsers':
        return (
          <div className={styles.audienceSection}>
            <div className={styles.audienceType}>
              <FontAwesomeIcon icon={faGlobe} className={styles.audienceIcon}/>
              <span className={styles.audienceLabel}>All Users</span>
            </div>
            <p className={styles.audienceDescription}>
              This message will be displayed to all users of the app.
            </p>
          </div>
        );
      default:
        return (
          <p>
            <span>Unknown audience configuration.</span>
          </p>
        );
    }
  };

  return (
    <DetailsCard onClick={onClick}>
      <h6>Audience</h6>
      {renderAudienceInfo()}
    </DetailsCard>
  );
};

const useStyles = createUseStyles({
  audienceSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  audienceType: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  audienceIcon: {
    color: Colors.darkGray2,
    fontSize: '16px',
  },
  audienceLabel: {
    fontWeight: 'bold',
    fontSize: '14px',
    color: Colors.white,
  },
  audienceDescription: {
    fontSize: '14px',
    margin: '4px 0',
    color: Colors.gray0,
  },
  employerList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    marginTop: '8px',
  },
  employerChip: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 12px',
    backgroundColor: Colors.darkGrayBackground,
    border: `1px solid ${Colors.darkGrayBorder}`,
    borderRadius: '16px',
    fontSize: '12px',
    color: Colors.white,
  },
  usersIcon: {
    color: Colors.darkGray2,
    fontSize: '14px',
  },
});

export default AudienceCard;
